import { useState } from "react";
import { PrimaryButton } from "@normasteaching/norma-ui";
import {
  BonificoOrderDTO,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { useGrantB2BCourses } from "../../ProductService/BonificoOrder/useGrantB2BCourses.ts";
import { B2BCourseGrantsEditor } from "./B2BCourseGrantsEditor.tsx";
import {
  B2BCourseGrantDraft,
  B2BCourseGrantDraftErrors,
  EMPTY_COURSE_GRANT_DRAFT,
  hasCourseGrantDraftErrors,
  toCourseGrantPayloads,
  validateCourseGrantDrafts,
} from "./b2bCourseGrantDrafts.ts";
import { OverlayDialog } from "../../UI/Modal/OverlayDialog.tsx";

export function GrantB2BCoursesModal({
  order,
  onGranted,
  onClose,
}: {
  order: BonificoOrderDTO;
  onGranted: () => void;
  onClose: () => void;
}) {
  const { grantCourses, loading } = useGrantB2BCourses();
  const [drafts, setDrafts] = useState<B2BCourseGrantDraft[]>([
    EMPTY_COURSE_GRANT_DRAFT,
  ]);
  const [errors, setErrors] = useState<B2BCourseGrantDraftErrors[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleGrant = async () => {
    setSubmitError(null);
    const draftErrors = validateCourseGrantDrafts(drafts);
    setErrors(draftErrors);
    if (drafts.length === 0) {
      setSubmitError("Aggiungi almeno un partecipante");
      return;
    }
    if (hasCourseGrantDraftErrors(draftErrors)) return;
    try {
      await grantCourses(order.orderId, toCourseGrantPayloads(drafts));
      onGranted();
    } catch (e) {
      setSubmitError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante l'erogazione dei corsi. Riprova.",
        ),
      );
    }
  };

  return (
    <OverlayDialog
      onClose={onClose}
      isClosable={!loading}
      panelClassName="bg-white rounded-2xl shadow-xl p-6 w-full max-w-4xl max-h-[85vh] overflow-y-auto flex flex-col gap-4"
    >
      <h2 className="text-xl font-bold">Eroga corsi</h2>
      <p className="text-sm text-gray-600">
        Ordine B2B di{" "}
        <strong>
          {order.businessInformation?.businessName ||
            `${order.customer.firstName} ${order.customer.lastName}`}
        </strong>
        : per ogni partecipante viene assegnato solo il corso scelto
      </p>
      <B2BCourseGrantsEditor
        value={drafts}
        onChange={setDrafts}
        errors={errors}
      />
      {submitError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {submitError}
        </p>
      )}
      <div className="flex gap-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
        >
          Annulla
        </button>
        <PrimaryButton
          label="Eroga corsi"
          onClick={handleGrant}
          loading={loading}
          disabled={loading}
        />
      </div>
    </OverlayDialog>
  );
}
